// 051 — Bloques de Parto/Aborto, Postparto, Morbilidad e Intervenciones
// (Fase 4, Tanda 1b, ver PLAN.md). `Formulario051.tsx` (Tanda 1b) lo inserta
// completo, después de `Bloques051Identificacion.tsx` — cierra la página 1.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BloquePostparto } from './Bloques051Controles';
import { CampoDictado } from './CampoDictado';
import { SiNoBotones } from './camposSimples';
import { BotonesOpcion, GrillaSiNo, GrillaSiNoSd } from './Grillas051';
import type { SiNoValor } from './camposSimplesDatos';
import { SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import {
  ACOMPANANTE_OPCIONES,
  COMPLICACIONES_OBSTETRICAS_OPCIONES,
  CORTICOIDES_OPCIONES,
  DESGARRO_GRADO_OPCIONES,
  EG_METODO_OPCIONES,
  HEMORRAGIA_TIPOS_OPCIONES,
  HEMORRAGIA_TRIMESTRE_OPCIONES,
  INFECCIONES_051_OPCIONES,
  INMUNOGLOBULINA_ANTI_RH_D_OPCIONES,
  INTERVENCIONES_051_OPCIONES,
  INICIO_PARTO_OPCIONES,
  NACIMIENTO_CONDICION_OPCIONES,
  NACIMIENTO_ESTABLECIMIENTO_OPCIONES,
  OTROS_TRASTORNOS_051_OPCIONES,
  POSICION_PARTO_OPCIONES,
  PRACTICAS_INTERCULTURALES_OPCIONES,
  PRESENTACION_OPCIONES,
  PROCEDIMIENTOS_HEMORRAGIA_OPCIONES,
  RECIBIO_PARTO_OPCIONES,
  TERMINACION_PARTO_OPCIONES,
  TIPO_EVENTO_OPCIONES,
  TRASTORNOS_HIPERTENSIVOS_OPCIONES,
  TRASTORNOS_METABOLICOS_DIABETES_OPCIONES,
  TRASTORNOS_TIROIDEOS_OPCIONES,
  UTEROINHIBIDORES_OPCIONES,
  type Acompanante,
  type CorticoidesAntenatales,
  type DesgarroGrado,
  type EgMetodo,
  type EstadoIntervenciones051,
  type EstadoMorbilidad051,
  type EstadoParto051,
  type EstadoPartoAborto051,
  type InmunoglobulinaAntiRhD,
  type InicioParto,
  type NacimientoCondicion,
  type NacimientoEstablecimiento,
  type PosicionParto,
  type PresentacionParto,
  type TerminacionParto,
  type TipoEvento,
  type Uteroinhibidores,
} from './bloques051PartoDatos';

function CampoTexto({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
}

function CampoFecha({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function CampoSelect<T extends string>({
  id,
  label,
  value,
  opciones,
  etiquetaOpcion,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: T | '';
  readonly opciones: readonly T[];
  readonly etiquetaOpcion: (op: T) => string;
  readonly onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((op) => (
            <SelectItem key={op} value={op}>
              {etiquetaOpcion(op)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CampoCheckbox({ id, label, checked, onChange }: { readonly id: string; readonly label: string; readonly checked: boolean; readonly onChange: (v: boolean) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  );
}

function BloquePartoAborto({ estado, onChange }: { readonly estado: EstadoPartoAborto051; readonly onChange: (v: EstadoPartoAborto051) => void }): JSX.Element {
  const { t } = useTranslation();
  const p = 'formularios.f051.partoAborto';
  const textosSiNo = { si: t('formulario.si'), no: t('formulario.no') };
  function actualizar(cambios: Partial<EstadoPartoAborto051>): void {
    onChange({ ...estado, ...cambios });
  }
  const siNo = (id: string, clave: keyof EstadoPartoAborto051, valor: SiNoValor): JSX.Element => (
    <SiNoBotones id={`f051-pa-${id}`} etiqueta={t(`${p}.${clave}`)} valor={valor} textos={textosSiNo} onChange={(v) => actualizar({ [clave]: v })} />
  );
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${p}.titulo`)}</h4>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoSelect<TipoEvento>
          id="f051-pa-tipo-evento"
          label={t(`${p}.tipoEvento`)}
          value={estado.tipoEvento}
          opciones={TIPO_EVENTO_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.tipoEventoOpciones.${op}`)}
          onChange={(v) => actualizar({ tipoEvento: v })}
        />
        <CampoFecha id="f051-pa-fecha-ingreso" label={t(`${p}.fechaIngreso`)} value={estado.fechaIngreso} onChange={(v) => actualizar({ fechaIngreso: v })} />
        <CampoTexto id="f051-pa-consultas-prenatales" label={t(`${p}.consultasPrenatales`)} value={estado.consultasPrenatales} onChange={(v) => actualizar({ consultasPrenatales: v })} />
        {siNo('hospitalizacion', 'hospitalizacionEmbarazo', estado.hospitalizacionEmbarazo)}
        <CampoTexto id="f051-pa-hospitalizacion-dias" label={t(`${p}.hospitalizacionEmbarazoDias`)} value={estado.hospitalizacionEmbarazoDias} onChange={(v) => actualizar({ hospitalizacionEmbarazoDias: v })} />
        {siNo('carne', 'carne', estado.carne)}
        <CampoSelect<CorticoidesAntenatales>
          id="f051-pa-corticoides"
          label={t(`${p}.corticoidesAntenatales`)}
          value={estado.corticoidesAntenatales}
          opciones={CORTICOIDES_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.corticoidesAntenatalesOpciones.${op}`)}
          onChange={(v) => actualizar({ corticoidesAntenatales: v })}
        />
        <CampoTexto id="f051-pa-corticoides-semana" label={t(`${p}.corticoidesSemanaInicio`)} value={estado.corticoidesSemanaInicio} onChange={(v) => actualizar({ corticoidesSemanaInicio: v })} />
        <CampoSelect<Uteroinhibidores>
          id="f051-pa-uteroinhibidores"
          label={t(`${p}.uteroinhibidores`)}
          value={estado.uteroinhibidores}
          opciones={UTEROINHIBIDORES_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.uteroinhibidoresOpciones.${op}`)}
          onChange={(v) => actualizar({ uteroinhibidores: v })}
        />
        <CampoTexto id="f051-pa-uteroinhibidores-semana" label={t(`${p}.uteroinhibidoresSemanaInicio`)} value={estado.uteroinhibidoresSemanaInicio} onChange={(v) => actualizar({ uteroinhibidoresSemanaInicio: v })} />
        <CampoSelect<InicioParto>
          id="f051-pa-inicio"
          label={t(`${p}.inicio`)}
          value={estado.inicio}
          opciones={INICIO_PARTO_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.inicioOpciones.${op}`)}
          onChange={(v) => actualizar({ inicio: v })}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {siNo('ruptura', 'rupturaMembranasAnteparto', estado.rupturaMembranasAnteparto)}
        <CampoFecha id="f051-pa-ruptura-fecha" label={t(`${p}.rupturaMembranasFecha`)} value={estado.rupturaMembranasFecha} onChange={(v) => actualizar({ rupturaMembranasFecha: v })} />
        <CampoTexto id="f051-pa-ruptura-hora" label={t(`${p}.rupturaMembranasHora`)} value={estado.rupturaMembranasHora} onChange={(v) => actualizar({ rupturaMembranasHora: v })} />
        <CampoTexto id="f051-pa-ruptura-temp" label={t(`${p}.rupturaMembranasTemperatura`)} value={estado.rupturaMembranasTemperatura} onChange={(v) => actualizar({ rupturaMembranasTemperatura: v })} />
        {siNo('ruptura-menor37', 'rupturaMembranasMenor37Semanas', estado.rupturaMembranasMenor37Semanas)}
        {siNo('ruptura-mayor18h', 'rupturaMembranasMayorIgual18h', estado.rupturaMembranasMayorIgual18h)}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoTexto id="f051-pa-eg-semanas" label={t(`${p}.egSemanas`)} value={estado.egSemanas} onChange={(v) => actualizar({ egSemanas: v })} />
        <CampoTexto id="f051-pa-eg-dias" label={t(`${p}.egDias`)} value={estado.egDias} onChange={(v) => actualizar({ egDias: v })} />
        <CampoSelect<EgMetodo>
          id="f051-pa-eg-metodo"
          label={t(`${p}.egMetodo`)}
          value={estado.egMetodo}
          opciones={EG_METODO_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.egMetodoOpciones.${op}`)}
          onChange={(v) => actualizar({ egMetodo: v })}
        />
        <CampoSelect<PresentacionParto>
          id="f051-pa-presentacion"
          label={t(`${p}.presentacionSituacion`)}
          value={estado.presentacionSituacion}
          opciones={PRESENTACION_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.presentacionSituacionOpciones.${op}`)}
          onChange={(v) => actualizar({ presentacionSituacion: v })}
        />
        <CampoTexto id="f051-pa-peso-producto" label={t(`${p}.pesoProducto`)} value={estado.pesoProducto} onChange={(v) => actualizar({ pesoProducto: v })} />
        <CampoCheckbox id="f051-pa-peso-na" label={t(`${p}.noAplica`)} checked={estado.pesoProductoNoAplica} onChange={(v) => actualizar({ pesoProductoNoAplica: v })} />
        {siNo('tamano-fetal', 'tamanoFetalAcordeEg', estado.tamanoFetalAcordeEg)}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CampoSelect<Acompanante>
          id="f051-pa-acompanante-tdp"
          label={t(`${p}.acompananteTdp`)}
          value={estado.acompananteTdp}
          opciones={ACOMPANANTE_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.acompananteOpciones.${op}`)}
          onChange={(v) => actualizar({ acompananteTdp: v })}
        />
        <CampoSelect<Acompanante>
          id="f051-pa-acompanante-parto"
          label={t(`${p}.acompananteParto`)}
          value={estado.acompananteParto}
          opciones={ACOMPANANTE_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.acompananteOpciones.${op}`)}
          onChange={(v) => actualizar({ acompananteParto: v })}
        />
      </div>

      <GrillaSiNo
        titulo={t(`${p}.practicasInterculturales.titulo`)}
        claves={PRACTICAS_INTERCULTURALES_OPCIONES}
        prefijoI18n={`${p}.practicasInterculturales.opciones`}
        prefijoId="f051-pa-practicas"
        valores={estado.practicasInterculturales}
        onChange={(v) => actualizar({ practicasInterculturales: v })}
      />
      <CampoTexto id="f051-pa-practicas-cual" label={t(`${p}.practicasInterculturalesCual`)} value={estado.practicasInterculturalesCual} onChange={(v) => actualizar({ practicasInterculturalesCual: v })} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoSelect<PosicionParto>
          id="f051-pa-posicion"
          label={t(`${p}.posicionParto`)}
          value={estado.posicionParto}
          opciones={POSICION_PARTO_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.posicionPartoOpciones.${op}`)}
          onChange={(v) => actualizar({ posicionParto: v })}
        />
        <CampoSelect<NacimientoEstablecimiento>
          id="f051-pa-nacimiento-establecimiento"
          label={t(`${p}.nacimientoEstablecimiento`)}
          value={estado.nacimientoEstablecimiento}
          opciones={NACIMIENTO_ESTABLECIMIENTO_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.nacimientoEstablecimientoOpciones.${op}`)}
          onChange={(v) => actualizar({ nacimientoEstablecimiento: v })}
        />
        <CampoFecha id="f051-pa-nacimiento-fecha" label={t(`${p}.nacimientoFecha`)} value={estado.nacimientoFecha} onChange={(v) => actualizar({ nacimientoFecha: v })} />
        <CampoTexto id="f051-pa-nacimiento-hora" label={t(`${p}.nacimientoHora`)} value={estado.nacimientoHora} onChange={(v) => actualizar({ nacimientoHora: v })} />
        <CampoSelect<NacimientoCondicion>
          id="f051-pa-nacimiento-condicion"
          label={t(`${p}.nacimientoCondicion`)}
          value={estado.nacimientoCondicion}
          opciones={NACIMIENTO_CONDICION_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.nacimientoCondicionOpciones.${op}`)}
          onChange={(v) => actualizar({ nacimientoCondicion: v })}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {siNo('episiotomia', 'episiotomia', estado.episiotomia)}
        {siNo('desgarro', 'desgarro', estado.desgarro)}
        <CampoSelect<DesgarroGrado>
          id="f051-pa-desgarro-grado"
          label={t(`${p}.desgarroGrado`)}
          value={estado.desgarroGrado}
          opciones={DESGARRO_GRADO_OPCIONES}
          etiquetaOpcion={(op) => op}
          onChange={(v) => actualizar({ desgarroGrado: v })}
        />
        {siNo('manejo-activo', 'manejoActivo', estado.manejoActivo)}
        {siNo('multiple', 'multiple', estado.multiple)}
        <CampoTexto id="f051-pa-multiple-orden" label={t(`${p}.multipleOrden`)} value={estado.multipleOrden} onChange={(v) => actualizar({ multipleOrden: v })} />
        <CampoSelect<TerminacionParto>
          id="f051-pa-terminacion"
          label={t(`${p}.terminacion`)}
          value={estado.terminacion}
          opciones={TERMINACION_PARTO_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.terminacionOpciones.${op}`)}
          onChange={(v) => actualizar({ terminacion: v })}
        />
      </div>

      <CampoTexto id="f051-pa-indicacion-principal" label={t(`${p}.indicacionPrincipal`)} value={estado.indicacionPrincipal} onChange={(v) => actualizar({ indicacionPrincipal: v })} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SelectorDiagnosticoCie10 label={t(`${p}.cieInduccion`)} value={estado.cieInduccion} onChange={(v) => actualizar({ cieInduccion: v })} />
        <SelectorDiagnosticoCie10 label={t(`${p}.cieOperatoria`)} value={estado.cieOperatoria} onChange={(v) => actualizar({ cieOperatoria: v })} />
      </div>

      <GrillaSiNo
        titulo={t(`${p}.recibio.titulo`)}
        claves={RECIBIO_PARTO_OPCIONES}
        prefijoI18n={`${p}.recibio.opciones`}
        prefijoId="f051-pa-recibio"
        valores={estado.recibio}
        onChange={(v) => actualizar({ recibio: v })}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {siNo('placenta-completa', 'placentaCompleta', estado.placentaCompleta)}
        {siNo('placenta-retenida', 'placentaRetenida', estado.placentaRetenida)}
        {siNo('ligadura-cordon', 'ligaduraCordonAlCesarPulsaciones', estado.ligaduraCordonAlCesarPulsaciones)}
        {siNo('medicacion', 'medicacion', estado.medicacion)}
        <CampoTexto id="f051-pa-medicacion-cual" label={t(`${p}.medicacionCual`)} value={estado.medicacionCual} onChange={(v) => actualizar({ medicacionCual: v })} />
      </div>
    </div>
  );
}

/** Cabecera no/si de un grupo de morbilidad; con "No" el papel deja el grupo sin ítems, así que aquí tampoco se muestran. */
function GrupoMorbilidad({
  id,
  titulo,
  presente,
  onPresente,
  children,
}: {
  readonly id: string;
  readonly titulo: string;
  readonly presente: SiNoValor;
  readonly onPresente: (v: SiNoValor) => void;
  readonly children: JSX.Element | (JSX.Element | null)[] | null;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 rounded-md border border-hce-line p-3">
      <SiNoBotones id={id} etiqueta={titulo} valor={presente} onChange={onPresente} textos={{ si: t('formulario.si'), no: t('formulario.no') }} />
      {presente === 'no' ? null : children}
    </div>
  );
}

function BloqueMorbilidad({ estado, onChange }: { readonly estado: EstadoMorbilidad051; readonly onChange: (v: EstadoMorbilidad051) => void }): JSX.Element {
  const { t } = useTranslation();
  const m = 'formularios.f051.morbilidad';
  function actualizar(cambios: Partial<EstadoMorbilidad051>): void {
    onChange({ ...estado, ...cambios });
  }
  const textosSiNo = { si: t('formulario.si'), no: t('formulario.no') };
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${m}.titulo`)}</h4>

      <GrupoMorbilidad id="f051-mb-hipertensivos-presente" titulo={t(`${m}.trastornosHipertensivos.titulo`)} presente={estado.trastornosHipertensivosPresente} onPresente={(v) => actualizar({ trastornosHipertensivosPresente: v })}>
        <GrillaSiNo claves={TRASTORNOS_HIPERTENSIVOS_OPCIONES} prefijoI18n={`${m}.trastornosHipertensivos.opciones`} prefijoId="f051-mb-hipertensivos" valores={estado.trastornosHipertensivos} onChange={(v) => actualizar({ trastornosHipertensivos: v })} />
      </GrupoMorbilidad>

      <GrupoMorbilidad id="f051-mb-infecciones-presente" titulo={t(`${m}.infecciones.titulo`)} presente={estado.infeccionesPresente} onPresente={(v) => actualizar({ infeccionesPresente: v })}>
        <GrillaSiNo claves={INFECCIONES_051_OPCIONES} prefijoI18n={`${m}.infecciones.opciones`} prefijoId="f051-mb-infecciones" valores={estado.infecciones} onChange={(v) => actualizar({ infecciones: v })} />
        {estado.infecciones.otraInfeccion === 'si' ? <CampoTexto id="f051-mb-otra-infeccion-cual" label={t(`${m}.otraInfeccionCual`)} value={estado.otraInfeccionCual} onChange={(v) => actualizar({ otraInfeccionCual: v })} /> : null}
      </GrupoMorbilidad>

      <div className="flex flex-col gap-2 rounded-md border border-hce-line p-3">
        <Label>{t(`${m}.hemorragiaTipos.titulo`)}</Label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {HEMORRAGIA_TRIMESTRE_OPCIONES.map((tr) => (
            <SiNoBotones
              key={tr}
              id={`f051-mb-hemorragia-trim-${tr}`}
              etiqueta={t(`${m}.hemorragiaTrimestreOpciones.${tr}`)}
              valor={estado.hemorragiaTrimestres[tr] ?? ''}
              textos={textosSiNo}
              onChange={(v) => actualizar({ hemorragiaTrimestres: { ...estado.hemorragiaTrimestres, [tr]: v } })}
            />
          ))}
        </div>
        <GrillaSiNo claves={HEMORRAGIA_TIPOS_OPCIONES} prefijoI18n={`${m}.hemorragiaTipos.opciones`} prefijoId="f051-mb-hemorragia" valores={estado.hemorragiaTipos} onChange={(v) => actualizar({ hemorragiaTipos: v })} />
      </div>

      <BotonesOpcion
        id="f051-mb-rh-d"
        etiqueta={t(`${m}.inmunoglobulinaAntiRhD`)}
        valor={estado.inmunoglobulinaAntiRhD}
        opciones={INMUNOGLOBULINA_ANTI_RH_D_OPCIONES.map((o) => ({ valor: o, texto: t(`${m}.inmunoglobulinaAntiRhDOpciones.${o}`) }))}
        onChange={(v) => actualizar({ inmunoglobulinaAntiRhD: v as InmunoglobulinaAntiRhD | '' })}
      />

      <GrupoMorbilidad id="f051-mb-metabolicos-presente" titulo={t(`${m}.trastornosMetabolicos`)} presente={estado.trastornosMetabolicosPresente} onPresente={(v) => actualizar({ trastornosMetabolicosPresente: v })}>
        <GrupoMorbilidad id="f051-mb-diabetes-presente" titulo={t(`${m}.trastornosMetabolicosDiabetes.titulo`)} presente={estado.diabetesPresente} onPresente={(v) => actualizar({ diabetesPresente: v })}>
          <GrillaSiNo claves={TRASTORNOS_METABOLICOS_DIABETES_OPCIONES} prefijoI18n={`${m}.trastornosMetabolicosDiabetes.opciones`} prefijoId="f051-mb-diabetes" valores={estado.trastornosMetabolicosDiabetes} onChange={(v) => actualizar({ trastornosMetabolicosDiabetes: v })} />
        </GrupoMorbilidad>
        <GrupoMorbilidad id="f051-mb-tiroideos-presente" titulo={t(`${m}.trastornosTiroideos.titulo`)} presente={estado.tiroideosPresente} onPresente={(v) => actualizar({ tiroideosPresente: v })}>
          <GrillaSiNo claves={TRASTORNOS_TIROIDEOS_OPCIONES} prefijoI18n={`${m}.trastornosTiroideos.opciones`} prefijoId="f051-mb-tiroideos" valores={estado.trastornosTiroideos} onChange={(v) => actualizar({ trastornosTiroideos: v })} />
          {estado.trastornosTiroideos.otro === 'si' ? <CampoTexto id="f051-mb-otro-metabolico-cual" label={t(`${m}.otroTrastornoMetabolicoCual`)} value={estado.otroTrastornoMetabolicoCual} onChange={(v) => actualizar({ otroTrastornoMetabolicoCual: v })} /> : null}
        </GrupoMorbilidad>
      </GrupoMorbilidad>

      <GrupoMorbilidad id="f051-mb-otros-presente" titulo={t(`${m}.otrosTrastornos.titulo`)} presente={estado.otrosTrastornosPresente} onPresente={(v) => actualizar({ otrosTrastornosPresente: v })}>
        <GrillaSiNo claves={OTROS_TRASTORNOS_051_OPCIONES} prefijoI18n={`${m}.otrosTrastornos.opciones`} prefijoId="f051-mb-otros" valores={estado.otrosTrastornos} onChange={(v) => actualizar({ otrosTrastornos: v })} />
      </GrupoMorbilidad>

      <GrupoMorbilidad id="f051-mb-complicaciones-presente" titulo={t(`${m}.complicacionesObstetricas.titulo`)} presente={estado.complicacionesPresente} onPresente={(v) => actualizar({ complicacionesPresente: v })}>
        <GrillaSiNo claves={COMPLICACIONES_OBSTETRICAS_OPCIONES} prefijoI18n={`${m}.complicacionesObstetricas.opciones`} prefijoId="f051-mb-complicaciones" valores={estado.complicacionesObstetricas} onChange={(v) => actualizar({ complicacionesObstetricas: v })} />
        {estado.complicacionesObstetricas.otros === 'si' ? <CampoTexto id="f051-mb-complicaciones-otros-cual" label={t(`${m}.complicacionesOtrosCual`)} value={estado.complicacionesOtrosCual} onChange={(v) => actualizar({ complicacionesOtrosCual: v })} /> : null}
      </GrupoMorbilidad>
    </div>
  );
}

function BloqueIntervenciones({ estado, onChange }: { readonly estado: EstadoIntervenciones051; readonly onChange: (v: EstadoIntervenciones051) => void }): JSX.Element {
  const { t } = useTranslation();
  const p = 'formularios.f051.intervenciones';
  function actualizar(cambios: Partial<EstadoIntervenciones051>): void {
    onChange({ ...estado, ...cambios });
  }
  const si = (c: string): boolean => estado.intervenciones[c] === 'si';
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${p}.titulo`)}</h4>
      <GrillaSiNoSd claves={INTERVENCIONES_051_OPCIONES} prefijoI18n={p} prefijoId="f051-iv" valores={estado.intervenciones} onChange={(v) => actualizar({ intervenciones: v as EstadoIntervenciones051['intervenciones'] })} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        {si('uterotonicosTtoHemorragia') ? <CampoTexto id="f051-iv-uterotonicos-cual" label={t(`${p}.uterotonicosCual`)} value={estado.uterotonicosCual} onChange={(v) => actualizar({ uterotonicosCual: v })} /> : null}
        {si('administracionHemoderivados') ? <CampoTexto id="f051-iv-hemoderivados-vol" label={t(`${p}.hemoderivadosVolumenes`)} value={estado.hemoderivadosVolumenes} onChange={(v) => actualizar({ hemoderivadosVolumenes: v })} /> : null}
        {si('ingresoUci7Dias') ? <CampoTexto id="f051-iv-uci-dias" label={t(`${p}.ingresoUciDias`)} value={estado.ingresoUciDias} onChange={(v) => actualizar({ ingresoUciDias: v })} /> : null}
        {si('usoAtbIv') ? <CampoTexto id="f051-iv-atb-cual" label={t(`${p}.usoAtbIvCual`)} value={estado.usoAtbIvCual} onChange={(v) => actualizar({ usoAtbIvCual: v })} /> : null}
      </div>
      <div className="flex flex-col gap-1">
        <Label>{t(`${p}.procedimientosHemorragia.titulo`)}</Label>
        <GrillaSiNoSd
          claves={PROCEDIMIENTOS_HEMORRAGIA_OPCIONES}
          prefijoI18n={`${p}.procedimientosHemorragia.opciones`}
          prefijoId="f051-iv-hemorragia"
          valores={estado.procedimientosHemorragia}
          onChange={(v) => actualizar({ procedimientosHemorragia: v as EstadoIntervenciones051['procedimientosHemorragia'] })}
        />
      </div>
    </div>
  );
}

export interface Bloques051PartoProps {
  readonly estado: EstadoParto051;
  readonly onChange: (estado: EstadoParto051) => void;
}

export function Bloques051Parto({ estado, onChange }: Bloques051PartoProps): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <BloquePartoAborto estado={estado.partoAborto} onChange={(v) => onChange({ ...estado, partoAborto: v })} />
      <BloquePostparto estado={estado.postparto} onChange={(v) => onChange({ ...estado, postparto: v })} />
      <BloqueMorbilidad estado={estado.morbilidad} onChange={(v) => onChange({ ...estado, morbilidad: v })} />
      <BloqueIntervenciones estado={estado.intervenciones} onChange={(v) => onChange({ ...estado, intervenciones: v })} />
    </div>
  );
}
